import { Box, Grid } from '@mui/material';
import React, { useCallback } from 'react';
import { colors } from 'spw-theme';
import styled from 'styled-components';
import {
  selectBenchmarkReportSummary,
  selectCurrentBechmarkReport,
  selectSelectedBenchmarkProjects,
} from 'spw-redux/modules/benchmark-reports/selectors';
import { useSelector } from 'react-redux';
import { BenchmarkReport } from 'spw-redux/modules/benchmark-reports/types';
import { BenchmarkReportCardExportables } from 'spw-redux/modules/reports/types';
import BenchmarkReportOverviewCard from './report-overview';
import IndividualWorkspacesAnalysis from './individual-workspaces-analysis';
import USFAllocationBySpaceCategory from './usf-allocation-by-space-category';
import WorkSeatRatios from './work-seat-ratios';
import USFMetrics from './usf-metrics';
import BenchmarkReportStatusIndicator from '../../status-indicator';

const border = `1px solid ${colors.veryLightGray}`;

const CardContainer = styled(Box)`
  padding: ${(props: { $padding?: string }) => props.$padding || '16px 24px'};
  box-sizing: border-box;
  .title {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 20px;
  }
`;

type Props = {
  isRenderingForExport?: boolean;
  visibleCardForExportable?: BenchmarkReportCardExportables;
};

const ExportableHeaderContainer = styled(Box)`
  border-bottom: ${border};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  padding: 8px 16px 4px 16px;
  font-size: 14px;
  b {
    font-weight: 500;
  }
`;

const ExportablesHeader = ({ report }: { report: BenchmarkReport }) => {
  return (
    <ExportableHeaderContainer>
      <Box mr={6}>
        <b>REPORT NAME:</b> {report?.name}
      </Box>
      <BenchmarkReportStatusIndicator verified={report?.verified} />
    </ExportableHeaderContainer>
  );
};

const BenchmarkReportSummaryCards = ({
  isRenderingForExport,
  visibleCardForExportable,
}: Props) => {
  const selectedProjects = useSelector(selectSelectedBenchmarkProjects);
  const summary = useSelector(selectBenchmarkReportSummary);
  const currentReport = useSelector(selectCurrentBechmarkReport);

  const cardIsVisible = useCallback(
    (card: BenchmarkReportCardExportables) => {
      return !visibleCardForExportable || visibleCardForExportable === card;
    },
    [visibleCardForExportable]
  );

  return (
    <>
      {isRenderingForExport && !visibleCardForExportable ? (
        <ExportablesHeader report={currentReport} />
      ) : null}
      <Grid container columnSpacing={0} rowGap={0}>
        {cardIsVisible(BenchmarkReportCardExportables.reportOverview) && (
          <Grid item xs={6}>
            <CardContainer
              height={312}
              borderRight={border}
              borderBottom={border}
              borderTop={border}
            >
              <BenchmarkReportOverviewCard
                benchmarkProjects={selectedProjects}
                summary={summary}
              />
            </CardContainer>
          </Grid>
        )}
        {cardIsVisible(
          BenchmarkReportCardExportables.individualWorkspacesAnalysis
        ) && (
          <Grid item xs={6}>
            <CardContainer
              height={312}
              borderBottom={border}
              borderTop={border}
              $padding="16px 56px"
            >
              <IndividualWorkspacesAnalysis
                individualWorkspacesAnalysis={
                  summary?.individualWorkspacesAnalysis
                }
              />
            </CardContainer>
          </Grid>
        )}
        {cardIsVisible(BenchmarkReportCardExportables.USFAllocation) && (
          <Grid item xs={6}>
            <CardContainer height={464} borderRight={border}>
              <USFAllocationBySpaceCategory
                allocationBySpaceCategory={
                  summary?.usfAllocationBySpaceCategory
                }
              />
            </CardContainer>
          </Grid>
        )}
        <Grid item xs={6} display="flex" flexDirection="column">
          {cardIsVisible(BenchmarkReportCardExportables.workSeatRatios) && (
            <CardContainer height={302} borderBottom={border}>
              <WorkSeatRatios ratios={summary?.workSeatRatios} />
            </CardContainer>
          )}
          {cardIsVisible(BenchmarkReportCardExportables.USFMetrics) && (
            <CardContainer height={146}>
              <USFMetrics metrics={summary?.usfMetrics} />
            </CardContainer>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default BenchmarkReportSummaryCards;
